import {
    ArticleTarifDTOJsonldCatalogueReadCatalogueReadInterne,
    CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
    RubriqueTarifDTOJsonldCatalogueReadCatalogueReadInterne,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { emptyIcon, FaOptionIcon, waitFormalisteIcon } from '@europrocurement/l2d-icons';
import { Box, Typography, useTheme } from '@mui/material';
import React, { ReactNode, useRef, useState } from 'react';
import ActionMenuItems from '../Menu/ActionMenuItems';

export type TarifCellProps = {
    type: 'honoraire' | 'frais' | 'annonce';
    row: CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull;
    editable?: boolean;
    idPrescriber?: number;
    showChips?: boolean;
    onClick?: (
        e: React.MouseEvent,
        row: CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
        surchargeType: 'prescripteur' | 'catalogue' | 'package' | null,
    ) => void;
};

const TarifCell: React.FunctionComponent<TarifCellProps> = function ({
    type,
    row,
    editable = false,
    idPrescriber = undefined,
    showChips = true,
    onClick,
}) {
    const { palette } = useTheme();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuRef = useRef<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    function getTarif(): ReactNode {
        const rubriques = row.infosTarif?.rubriques ?? {};
        const safeGetTarif = (key: string) => {
            const rubrique = rubriques[key];
            if (!rubrique) {
                return (
                    <FaOptionIcon
                        size="sm"
                        {...emptyIcon.props}
                    />
                );
            }

            if (rubrique.tarifTotalRubFac != null) {
                return (
                    <>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {parseFloat(rubrique.tarifTotalRubFac ?? '0').toFixed(2)}
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            €
                        </Typography>
                        {rubrique.tarifACompleter ?? (
                            <FaOptionIcon
                                size="sm"
                                {...waitFormalisteIcon.props}
                            />
                        )}
                    </>
                );
            }

            return (
                <FaOptionIcon
                    size="sm"
                    {...waitFormalisteIcon.props}
                />
            );
        };

        switch (type) {
            case 'honoraire': {
                return safeGetTarif('VAC');
            }
            case 'frais': {
                const excludedKeys = ['VAC', 'PUB'];
                const totalFrais = Object.entries(rubriques)
                    .filter(([key]) => !excludedKeys.includes(key))
                    .reduce(
                        (sum, [, rubrique]) => sum + parseFloat(rubrique?.tarifTotalRubFac ?? '0'),
                        0,
                    );

                const hasTarifACompleter = Object.entries(rubriques)
                    .filter(([key]) => !excludedKeys.includes(key))
                    .some(([, rubrique]) => Boolean(rubrique?.tarifACompleter));
                return (
                    <>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {totalFrais.toFixed(2)}
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            €
                        </Typography>
                        {hasTarifACompleter ?? (
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                *
                            </Typography>
                        )}
                    </>
                );
            }
            case 'annonce': {
                return safeGetTarif('PUB');
            }
            default: {
                return (
                    <>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            0.00
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            €
                        </Typography>
                    </>
                );
            }
        }
    }

    function getHighestSurcharge(): 'prescripteur' | 'catalogue' | 'package' | null {
        const rubriques = row.infosTarif?.rubriques as {
            [key: string]: RubriqueTarifDTOJsonldCatalogueReadCatalogueReadInterne;
        };
        if (!rubriques) return null;

        const safeGetArticles = (key: string) =>
            rubriques[key]
                ? (Object.values(
                      rubriques[key]?.articles || {},
                  ) as ArticleTarifDTOJsonldCatalogueReadCatalogueReadInterne[])
                : [];

        let listArticle: ArticleTarifDTOJsonldCatalogueReadCatalogueReadInterne[] = [];

        switch (type) {
            case 'honoraire': {
                listArticle = safeGetArticles('VAC');
                break;
            }
            case 'frais': {
                const excludedKeys = ['VAC', 'PUB'];
                listArticle = Object.entries(rubriques)
                    .filter(([key]) => !excludedKeys.includes(key))
                    .flatMap(([, rubrique]) => Object.values(rubrique.articles || {}));
                break;
            }
            case 'annonce': {
                listArticle = safeGetArticles('PUB');
                break;
            }
            default: {
                return null;
            }
        }

        let highestSurcharge: 'prescripteur' | 'catalogue' | 'package' | null = null;

        const surcharges = listArticle
            .map((article) => {
                if (article.isSurchargePrescripteur)
                    return { surcharge: 'prescripteur', priority: 3 } as const;
                if (article.isSurchargeCatalogue)
                    return { surcharge: 'catalogue', priority: 2 } as const;
                if (article.isSurchargePackage)
                    return { surcharge: 'package', priority: 1 } as const;
                return null;
            })
            .filter((item) => item !== null)
            .sort((a, b) => b.priority - a.priority);

        if (surcharges.length > 0) {
            highestSurcharge = surcharges[0].surcharge;
        }

        return highestSurcharge;
    }

    function checkEditableCell(): boolean {
        if (
            (editable && getHighestSurcharge() !== null && type !== 'honoraire') ||
            (getHighestSurcharge() !== null && type === 'honoraire')
        ) {
            return true;
        }
        return false;
    }

    function getBackgroundColor(): string {
        const highestSurcharge = checkEditableCell() ? getHighestSurcharge() : '';

        switch (highestSurcharge) {
            case 'prescripteur':
                return theme.palette.brown.light;
            case 'catalogue':
                return theme.palette.purple.light;
            case 'package':
                return theme.palette.orange.light;
            default:
                return palette.grey[100];
        }
    }
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (checkEditableCell()) {
            setAnchorEl(event.currentTarget);
        }
        if (onClick) {
            onClick(event, row, getHighestSurcharge());
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                data-testid="tarif-box"
                display="flex"
                flexDirection="row"
                gap="2px"
                onClick={handleClick}
                ref={menuRef}
                sx={{
                    padding: '8px 4px',
                    borderRadius: '12px',
                    alignItems: 'center',
                    position: 'relative',
                    cursor: checkEditableCell() ? 'pointer' : 'default',
                    backgroundColor: showChips ? getBackgroundColor() : 'none',
                    color: 'text.gris70',
                    '&:hover .icon-modify': {
                        opacity: checkEditableCell() ? 1 : 0,
                        transform: checkEditableCell() ? 'scale(1)' : 'scale(0)',
                        width: checkEditableCell() ? 'auto' : 0,
                    },
                }}
            >
                {getTarif()}
                {checkEditableCell() && (
                    <Box
                        className="icon-modify"
                        data-testid="edit-icon"
                        sx={{
                            opacity: 0,
                            transform: 'scale(0)',
                            width: 0,
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <FaOptionIcon icon={['fad', 'pen']} />
                    </Box>
                )}
            </Box>
            <ActionMenuItems
                idPrescriber={idPrescriber}
                showPrescripteur={editable && type === 'honoraire'}
                showCatalogue={getHighestSurcharge() === 'catalogue'}
                showPackage={getHighestSurcharge() === 'package'}
                catalogue={row}
                close={handleClose}
                open={open}
                anchorEl={anchorEl}
            />
        </>
    );
};

export default TarifCell;
